import { useEffect, useRef, useState } from "react";
import Alert from "@/common/elements/Alerts";
import { useFieldVerification } from "@/contexts/FieldVerificationContext";

/**
 * Drop this anywhere inside a <FieldVerificationContext.Provider> — it shows
 * itself only when there's actually something pink to confirm, and explains
 * how to clear it (edit, or Ctrl+Q). No unverified fields, no banner.
 *
 * Dismissing it is purely visual — Save stays blocked either way. It
 * reappears on its own if a later jacket link introduces new unverified
 * fields after the previous batch was fully cleared.
 */
const VerificationHint = () => {
  const { requireVerification, hasUnverifiedFields, getUnverifiedFieldNames } =
    useFieldVerification();
  const [dismissed, setDismissed] = useState(false);
  const wasUnverified = useRef(false);

  const unverified = requireVerification && hasUnverifiedFields();

  useEffect(() => {
    if (unverified && !wasUnverified.current) {
      setDismissed(false);
    }
    wasUnverified.current = unverified;
  }, [unverified]);

  if (!unverified || dismissed) return null;

  const count = getUnverifiedFieldNames().length;

  return (
    <Alert
      show
      type="info"
      closable
      onClose={() => setDismissed(true)}
      style={{ marginBottom: 16 }}
      message={`${count} field${count === 1 ? "" : "s"} highlighted in pink ${
        count === 1 ? "needs" : "need"
      } to be verified`}
      description={
        <>
          These fields were filled in automatically from the selected record and
          have not been confirmed yet. Click into a pink field and press{" "}
          <strong>Alt + Q</strong> to confirm it without changing the value, or
          simply edit it — either one clears the highlight. Save is blocked
          until every pink field is confirmed.
        </>
      }
    />
  );
};

export default VerificationHint;
